"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = TextEditor;
var _code = require("@lexical/code");
var _link = require("@lexical/link");
var _list = require("@lexical/list");
var _markdown = require("@lexical/markdown");
var _LexicalComposer = require("@lexical/react/LexicalComposer");
var _LexicalContentEditable = require("@lexical/react/LexicalContentEditable");
var _LexicalErrorBoundary = _interopRequireDefault(require("@lexical/react/LexicalErrorBoundary"));
var _LexicalHistoryPlugin = require("@lexical/react/LexicalHistoryPlugin");
var _LexicalListPlugin = require("@lexical/react/LexicalListPlugin");
var _LexicalMarkdownShortcutPlugin = require("@lexical/react/LexicalMarkdownShortcutPlugin");
var _LexicalOnChangePlugin = require("@lexical/react/LexicalOnChangePlugin");
var _LexicalRichTextPlugin = require("@lexical/react/LexicalRichTextPlugin");
var _richText = require("@lexical/rich-text");
var _table = require("@lexical/table");
var _Box = _interopRequireDefault(require("@mui/material/Box/Box.js"));
var _react = require("react");
var _Toolbar = _interopRequireDefault(require("./Toolbar"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const UNDERLINE = {
  format: ['underline'],
  tag: '+',
  type: 'text-format'
};
const mdTransformers = [..._markdown.TRANSFORMERS, UNDERLINE];
const Placeholder = ({
  text = 'Enter some text...'
}) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Box.default, {
  sx: {
    display: 'inline-block',
    color: 'text.disabled',
    position: 'absolute',
    top: 8,
    left: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'none'
  },
  children: text
});
function TextEditor({
  value,
  minHeight = 150,
  editable = true,
  onChange = () => void {},
  placeholder
}) {
  const editorConfig = (0, _react.useMemo)(() => ({
    editable,
    namespace: 'text-editor',
    editorState: () => (0, _markdown.$convertFromMarkdownString)(value, mdTransformers),
    theme: {
      paragraph: 'editor-paragraph',
      text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
        strikethrough: 'editor-text-strikethrough',
        underlineStrikethrough: 'editor-text-underlineStrikethrough'
      }
    },
    onError(error) {
      console.error(error);
    },
    nodes: [_richText.HeadingNode, _list.ListNode, _list.ListItemNode, _code.CodeNode, _richText.QuoteNode, _table.TableNode, _table.TableCellNode, _table.TableRowNode, _link.AutoLinkNode, _link.LinkNode]
  }), []);
  const handleChange = editorState => {
    editorState.read(() => {
      const md = (0, _markdown.$convertToMarkdownString)(mdTransformers);
      onChange(md);
    });
  };
  const handleKeyDown = event => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event?.stopPropagation(); // Prevent the event from bubbling up to the slider
    }
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalComposer.LexicalComposer, {
    initialConfig: editorConfig,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Box.default, {
      sx: editable ? {
        border: 1,
        borderRadius: 2,
        borderColor: 'border.main'
      } : {},
      children: [editable && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Toolbar.default, {}), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Box.default, {
        sx: {
          p: 1,
          position: 'relative',
          '& .editor-input': {
            minHeight,
            outline: 0,
            '& .editor-paragraph': {
              margin: 0
            },
            '& .editor-text-bold': {
              fontWeight: 'bold'
            },
            '& .editor-text-italic': {
              fontStyle: 'italic'
            },
            '& .editor-text-underline': {
              textDecoration: 'underline'
            },
            '& .editor-text-strikethrough': {
              textDecoration: 'line-through'
            },
            '& .editor-text-underlineStrikethrough': {
              textDecoration: 'underline line-through'
            }
          }
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalRichTextPlugin.RichTextPlugin, {
          contentEditable: /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalContentEditable.ContentEditable, {
            className: "editor-input",
            autoFocus: true,
            onKeyDown: handleKeyDown
          }),
          placeholder: /*#__PURE__*/(0, _jsxRuntime.jsx)(Placeholder, {
            text: placeholder
          }),
          ErrorBoundary: _LexicalErrorBoundary.default
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalOnChangePlugin.OnChangePlugin, {
          ignoreSelectionChange: true,
          onChange: handleChange
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalListPlugin.ListPlugin, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalHistoryPlugin.HistoryPlugin, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_LexicalMarkdownShortcutPlugin.MarkdownShortcutPlugin, {
          transformers: mdTransformers
        })]
      })]
    })
  });
}